'use client'

import type { MenuItem, MenuSeparator } from '@graftcode/design-system'
import { Avatar, Icon } from '@graftcode/design-system'

import { SIDEBAR_TEST_IDS } from '@common/common-test-ids.const'
import { BASE_WORKSPACE_ITEMS } from '@common/const'
import { MenuActionId } from '@common/enums'
import { resolveWorkspaceLogoDisplayUrl } from '@common/helpers'

import type { Workspace } from '@modules/workspaces/workspaces.type'

export function getInitials(name?: string): string {
  if (!name) return ''

  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length >= 2) {
    const firstChar = words[0]!.charAt(0)
    const lastChar = words[words.length - 1]!.charAt(0)
    return (firstChar + lastChar).toUpperCase()
  }

  return name.substring(0, 2).toUpperCase()
}

export function getWorkspaceLogoUrl(workspace: Workspace | null | undefined): string | undefined {
  if (!workspace) return undefined
  return resolveWorkspaceLogoDisplayUrl(workspace.logoUrl)
}

export function createWorkspaceItems(
  workspaces: Workspace[],
  currentWorkspace: Workspace | null
): (MenuItem | MenuSeparator)[] {
  const workspacesForPicker =
    currentWorkspace && !workspaces.some((workspace) => workspace.workspaceId === currentWorkspace.workspaceId)
      ? [...workspaces, currentWorkspace]
      : workspaces

  const workspaceGroupItems = workspacesForPicker.map((workspace) => {
    const isSelected = currentWorkspace?.workspaceId === workspace.workspaceId
    const logoUrl = resolveWorkspaceLogoDisplayUrl(workspace.logoUrl)
    const initials = getInitials(workspace.name)

    return {
      type: 'item' as const,
      active: true,
      selected: isSelected,
      icon: logoUrl ? (
        <Avatar size={20} src={logoUrl} data-testid={SIDEBAR_TEST_IDS.menuItemAvatar} />
      ) : (
        <Avatar size={20} fallback={initials} data-testid={SIDEBAR_TEST_IDS.menuItemAvatar} />
      ),
      groups: [],
      label: workspace.name,
      id: `workspace-${workspace.workspaceId}`,
      size: 'xs' as const,
    }
  })

  const WORKSPACE_ITEM: MenuItem = {
    type: 'item',
    icon: Icon.OrganizationHorizontalRegular,
    active: workspaces.length > 0,
    label: 'Switch workspace',
    id: 'item-3',
    size: 'xs',
    groups: [
      {
        id: 'group-1',
        separatorAfter: true,
        items: workspaceGroupItems,
      },
      {
        id: 'group-2',
        items: [
          {
            type: 'item',
            icon: Icon.AddRegular,
            active: true,
            groups: [],
            label: 'Create Workspace',
            id: MenuActionId.CREATE_WORKSPACE,
            size: 'xs',
          },
        ],
      },
    ],
  }

  return [...BASE_WORKSPACE_ITEMS, WORKSPACE_ITEM]
}
